import { useEffect } from "react";
import confetti from "canvas-confetti";
import { Gift, Sparkles, X } from "lucide-react";
import { motion } from "framer-motion";
import { useLayout } from "@/context/LayoutContext";

export default function WelcomeRewardModal({ isOpen, onClose, coupon }) {
  const { theme } = useLayout();

  useEffect(() => {
    if (isOpen) {
      // Fire confetti!
      const duration = 3000;
      const end = Date.now() + duration;

      const colors = [theme.colors.primary, '#FFD700', '#FF6B6B', '#4ECDC4'];

      (function frame() {
        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: colors
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: colors
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      }());
    }
  }, [isOpen, theme.colors.primary]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: theme.colors.bgCard }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center z-10"
          style={{ backgroundColor: `${theme.colors.primary}20` }}
        >
          <X className="w-4 h-4" style={{ color: theme.colors.primary }} />
        </button>

        {/* Header with gradient */}
        <div 
          className="relative py-8 px-6 text-center overflow-hidden"
          style={{ 
            background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primary}dd 100%)`
          }}
        >
          {/* Animated sparkles */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 opacity-20"
            style={{
              background: "radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)",
              backgroundSize: "20px 20px"
            }}
          />
          
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
          >
            <Gift className="w-10 h-10 text-white" />
          </motion.div>

          <motion.h2 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-2xl font-black text-white"
          >
            WILLKOMMEN!
          </motion.h2>
          
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-white/80 text-sm mt-2"
          >
            Hier ist dein Starter-Geschenk
          </motion.p>
        </div>

        {/* Coupon Preview */}
        <div className="p-6">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="rounded-2xl overflow-hidden border-2 border-dashed"
            style={{ borderColor: theme.colors.primary }}
          >
            {coupon?.image_url && (
              <img 
                src={coupon.image_url}
                alt={coupon.title}
                className="w-full h-32 object-cover"
              />
            )}
            <div 
              className="p-4 text-center"
              style={{ backgroundColor: `${theme.colors.primary}10` }}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Sparkles className="w-4 h-4" style={{ color: theme.colors.primary }} />
                <span 
                  className="text-xs font-bold uppercase"
                  style={{ color: theme.colors.primary }}
                >
                  Exklusiv für dich
                </span>
                <Sparkles className="w-4 h-4" style={{ color: theme.colors.primary }} />
              </div>
              <h3 
                className="text-xl font-bold"
                style={{ color: theme.colors.textPrimary }}
              >
                {coupon?.title || "GRATIS WILLKOMMENS-DEAL"}
              </h3>
              <p 
                className="text-sm mt-1"
                style={{ color: theme.colors.textSecondary }}
              >
                {coupon?.description || "Dein erstes Geschenk ist freigeschaltet!"}
              </p>
            </div>
          </motion.div>

          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            onClick={onClose}
            className="w-full mt-6 py-4 rounded-2xl font-bold text-lg transition-all active:scale-95"
            style={{ 
              backgroundColor: theme.colors.primary,
              color: '#FFF'
            }}
          >
            Jetzt entdecken!
          </motion.button>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-center text-xs mt-4"
            style={{ color: theme.colors.textSecondary }}
          >
            Dein Geschenk wurde automatisch aktiviert
          </motion.p>
        </div>
      </motion.div>
    </div>
  );
}
